import { clsx, type ClassValue } from "clsx"
import { twMerge } from "tailwind-merge"
import { subDays, startOfMonth, endOfMonth, startOfYear, endOfYear, subMonths, subYears } from "date-fns"
import type { RelativeDateValue } from "@/data/types/views.types"
import type { DateRange } from "react-day-picker"

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

export function getRelativeDateRange(value: RelativeDateValue): DateRange {
  const now = new Date()
  switch (value) {
    case 'last_7_days':
      return { from: subDays(now, 6), to: now }
    case 'last_14_days':
      return { from: subDays(now, 13), to: now }
    case 'last_30_days':
      return { from: subDays(now, 29), to: now }
    case 'last_90_days':
      return { from: subDays(now, 89), to: now }
    case 'this_month':
      return { from: startOfMonth(now), to: now }
    case 'last_month': {
      const lastMonth = subMonths(now, 1)
      return { from: startOfMonth(lastMonth), to: endOfMonth(lastMonth) }
    }
    case 'this_year':
      return { from: startOfYear(now), to: now }
    case 'last_year': {
      const lastYear = subYears(now, 1)
      return { from: startOfYear(lastYear), to: endOfYear(lastYear) }
    }
    default:
      return { from: subDays(now, 29), to: now }
  }
}

export function formatDate(dateString: string) {
  const date = new Date(dateString)
  return date.toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
  })
}

export function formatDateTime(dateString: string) {
  const date = new Date(dateString)
  return date.toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  })
}


export function formatTimestamp(timestamp: string) {
  const date = new Date(timestamp)
  return date.toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  })
}

// Format currency with full precision for tooltips
export function formatCurrencyFull(value: number) {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 5,
    maximumFractionDigits: 6,
  }).format(value)
}

// Format currency with appropriate decimal places based on value size
export function formatCurrencyDisplay(value: number): { display: string; fullValue: string } {
  const fullValue = formatCurrencyFull(value)
  let minimumFractionDigits = 5
  let maximumFractionDigits = 5
  
  // If value is exactly 0, show 2 decimal places (e.g., "$0.00")
  // If value is greater than 0.1, show 2 decimal places
  // Otherwise show 5 decimal places for precision on small values
  if (value === 0 || value > 0.1) {
    minimumFractionDigits = 2
    maximumFractionDigits = 2
  }
  
  const display = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits,
    maximumFractionDigits,
  }).format(value)
  
  return {
    display,
    fullValue,
  }
}

// Simple currency format (legacy, use formatCurrencyDisplay for small value handling)
export function formatCurrency(value: number, minimumFractionDigits: number = 2, maximumFractionDigits: number = 2) {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: minimumFractionDigits,
    maximumFractionDigits: maximumFractionDigits,
  }).format(value)
}

// Format currency without decimals for display
export const formatCurrencyShort = (value: number) => {
  if (value >= 1000) {
    return `$${(value / 1000).toFixed(1)}k`;
  }
  return `$${value.toLocaleString()}`;
};